/**
 * Servicio de Validación de Tokens ERC-20
 * Valida que un contrato sea un token ERC-20 válido y obtiene su información
 */

import { ethers } from 'ethers';
import { Token } from '@/types';
import providerService from './provider.service';
import storageService from '@/services/wallet/storage.service';
import { getPopularTokens, DEPRECATED_TOKEN_ADDRESSES } from '@/config/tokens';

const NATIVE_ADDRESS = '0x0000000000000000000000000000000000000000';

// ABI mínimo para validar tokens ERC-20
const ERC20_MINIMAL_ABI = [
  'function name() view returns (string)',
  'function symbol() view returns (string)',
  'function decimals() view returns (uint8)',
  'function totalSupply() view returns (uint256)',
  'function balanceOf(address) view returns (uint256)',
];

export interface TokenValidationResult {
  isValid: boolean;
  token?: Partial<Token>;
  error?: string;
}

/**
 * Servicio de validación de tokens
 */
export const tokenValidationService = {
  /**
   * true si el contrato ya está en el sistema (nativo, popular, guardado, o deprecado).
   */
  isAddressAlreadyInSystem(address: string, networkId: string): boolean {
    const lower = address.toLowerCase();

    if (lower === NATIVE_ADDRESS) return true;
    if (DEPRECATED_TOKEN_ADDRESSES.some((a) => a.toLowerCase() === lower)) return true;
    if (getPopularTokens(networkId).some((t) => t.address.toLowerCase() === lower)) return true;

    return storageService
      .getCustomTokens()
      .some((t) => t.network === networkId && t.address.toLowerCase() === lower);
  },

  /**
   * Valida un token ERC-20 y obtiene su información
   * @param address - Dirección del contrato del token
   * @param networkId - ID de la red blockchain
   * @returns Resultado de la validación con información del token
   */
  async validateToken(address: string, networkId: string): Promise<TokenValidationResult> {
    try {
      // 1. Validar formato de dirección
      if (!address || !ethers.isAddress(address)) {
        return {
          isValid: false,
          error: 'Dirección de contrato inválida',
        };
      }

      // 2. Obtener provider
      const provider = providerService.getProvider(networkId);
      if (!provider) {
        return {
          isValid: false,
          error: `No se pudo conectar a la red ${networkId}`,
        };
      }

      // 3. Verificar que sea un contrato (tiene código)
      const code = await provider.getCode(address);
      if (code === '0x' || code === '0x0') {
        return {
          isValid: false,
          error: 'La dirección no es un contrato',
        };
      }

      // 4. Crear instancia del contrato
      const contract = new ethers.Contract(address, ERC20_MINIMAL_ABI, provider);

      // 5. Obtener información del token con timeout
      const timeout = 10000; // 10 segundos
      
      const [name, symbol, decimals] = await Promise.race([
        Promise.all([
          contract.name(),
          contract.symbol(),
          contract.decimals(),
        ]),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('Timeout obteniendo información del token')), timeout)
        ),
      ]);

      // 6. Validar que los datos sean correctos
      if (!symbol || typeof symbol !== 'string') {
        return {
          isValid: false,
          error: 'El contrato no tiene un símbolo válido',
        };
      }

      if (typeof decimals !== 'number' && typeof decimals !== 'bigint') {
        return {
          isValid: false,
          error: 'El contrato no tiene decimales válidos',
        };
      }

      // 7. Convertir decimals a número
      const decimalsNumber = typeof decimals === 'bigint' ? Number(decimals) : decimals;

      // 8. Validar rango de decimals (0-18 es lo común)
      if (decimalsNumber < 0 || decimalsNumber > 36) {
        return {
          isValid: false,
          error: `Decimales fuera de rango: ${decimalsNumber}`,
        };
      }

      // 9. Retornar token válido
      return {
        isValid: true,
        token: {
          address,
          symbol: symbol.trim(),
          name: name?.trim() || symbol.trim(),
          decimals: decimalsNumber,
          network: networkId,
          isNative: false,
        },
      };
    } catch (error: any) {
      console.error('[TokenValidation] Error validando token:', error);

      // Mensajes de error más amigables
      let errorMessage = 'Error al validar el token';

      if (error.message?.includes('timeout')) {
        errorMessage = 'Tiempo de espera agotado. La red puede estar lenta.';
      } else if (error.message?.includes('network')) {
        errorMessage = 'Error de conexión con la red';
      } else if (error.code === 'CALL_EXCEPTION') {
        errorMessage = 'El contrato no es un token ERC-20 válido';
      } else if (error.message) {
        errorMessage = error.message;
      }

      return {
        isValid: false,
        error: errorMessage,
      };
    }
  },

  /**
   * Valida múltiples tokens en lote
   * @param addresses - Array de direcciones de contratos
   * @param networkId - ID de la red blockchain
   * @returns Array de resultados de validación
   */
  async validateMultipleTokens(
    addresses: string[],
    networkId: string
  ): Promise<TokenValidationResult[]> {
    const results: TokenValidationResult[] = [];

    for (const address of addresses) {
      const result = await this.validateToken(address, networkId);
      results.push(result);
    }

    return results;
  },

  /**
   * Verifica si una dirección es un contrato
   * @param address - Dirección a verificar
   * @param networkId - ID de la red blockchain
   * @returns true si es un contrato, false si no
   */
  async isContract(address: string, networkId: string): Promise<boolean> {
    try {
      if (!ethers.isAddress(address)) {
        return false;
      }

      const provider = providerService.getProvider(networkId);
      if (!provider) {
        return false;
      }

      const code = await provider.getCode(address);
      return code !== '0x' && code !== '0x0';
    } catch (error) {
      console.error('[TokenValidation] Error verificando contrato:', error);
      return false;
    }
  },

  /**
   * Obtiene el balance de un token para una dirección
   * @param tokenAddress - Dirección del contrato del token
   * @param walletAddress - Dirección de la wallet
   * @param networkId - ID de la red blockchain
   * @returns Balance del token
   */
  async getTokenBalance(
    tokenAddress: string,
    walletAddress: string,
    networkId: string
  ): Promise<string> {
    try {
      const provider = providerService.getProvider(networkId);
      if (!provider) {
        throw new Error('Provider no disponible');
      }

      const contract = new ethers.Contract(
        tokenAddress,
        ['function balanceOf(address) view returns (uint256)'],
        provider
      );

      const balance = await contract.balanceOf(walletAddress);
      return balance.toString();
    } catch (error) {
      console.error('[TokenValidation] Error obteniendo balance:', error);
      return '0';
    }
  },
};

export default tokenValidationService;
